import React, { useEffect, useState } from 'react'
import i18n from 'i18next'
import { useTranslation, Trans } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { dataUriToFile } from 'ui/utils/imageUtils'
import { ImageProps } from 'types/common'

const pdfJsPath = '/tools/pdfjs-dist'

type ThumbnailProps = {
  image: ImageProps | File
  /**
   * 正方形枠のサイズ（px）※オプション機能
   */
  size?: number
  /**
   * 削除するときの関数
   */
  onDelete?: () => void
  /**
   * 削除ボタンラベル
   */
  deleteButtonLabel?: string
}

export const ThumbnailDefaultSize: number = 140

const fileNameText: (text: string, maxLength?: number) => string = (
  text,
  maxLength = 10
) => {
  const split = text.split('.')
  const extend = split.splice(split.length - 1, 1).join('')
  const name = split.join('.')
  return `${name.slice(0, maxLength)}...${extend}`
}

const useStyles = makeStyles<{ size?: number }>()((theme, { size }) => ({
  root: {
    textDecoration: 'none',
    display: 'inline-block',
    width: size,
  },
  photo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size && size - 2,
    height: size && size - 2,
  },
  img: {
    width: 'auto',
    height: 'auto',
    maxWidth: size && size - 2,
    maxHeight: size && size - 2,
  },
  delete: {
    display: 'flex',
    flexDirection: 'column',
  },
  deleteButton: {
    padding: '5px 7px',
    display: 'block',
    textAlign: 'right',
  },
  virusScanResult: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontSize: theme.typography.body2.fontSize,
    wordBreak: 'break-all',
  },
  infected: {
    background: theme.palette.divider,
    color: theme.palette.text.secondary,
  },
  yet: {
    background: theme.palette.text.disabled,
    color: theme.palette.background.paper,
  },
}))

export const Thumbnail: React.FC<ThumbnailProps> = ({
  image,
  size = ThumbnailDefaultSize,
  onDelete,
  deleteButtonLabel = i18n.t('削除'),
}: ThumbnailProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles({ size })
  const deleteHandler = (event: React.MouseEvent<HTMLElement>) => {
    onDelete && onDelete()
    event.preventDefault()
  }
  const [url, setUrl] = useState<string>()
  const [fileMimeType, setFileMimeType] = useState<string>()
  const fileName = image instanceof File ? image.name : image.file_name

  useEffect(() => {
    let objectUrl: string | undefined
    if (image instanceof File) {
      objectUrl = URL.createObjectURL(image)
      setUrl(objectUrl)
      setFileMimeType(image.type)
    } else {
      image.image &&
        dataUriToFile({
          dataUrlString: image.image,
          fileName,
          callback: (file) => {
            objectUrl = URL.createObjectURL(file)
            setUrl(objectUrl)
            setFileMimeType(file.type)
          },
        })
    }
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image])

  return (
    <Paper variant="outlined" square className={cx(onDelete && classes.delete)}>
      {(image instanceof File || image.image) && url && (
        <a
          href={url}
          title={fileName}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.root}
          onClick={(e) => {
            e.preventDefault()
            if (fileMimeType === 'application/pdf') {
              window.open(
                `${pdfJsPath}/web/viewer.html?file=${url}`,
                '_blank',
                'noopener,noreferrer'
              )
            } else {
              window.open(url, fileName)
            }
          }}
        >
          <div className={classes.photo}>
            {fileMimeType === 'application/pdf' ? (
              /* TODO: PDFプレビュー表示 */
              <PictureAsPdfIcon fontSize="large" />
            ) : (
              <img src={url} alt={fileName} className={classes.img} />
            )}
          </div>
        </a>
      )}
      {!(image instanceof File) && !image.image && (
        <div className={classes.photo}>
          {image.virus_scan_result === 'infected' && (
            <div className={cx(classes.virusScanResult, classes.infected)}>
              <div>
                {fileNameText(image.file_name)}
                {t('には問題があるため表示できません。')}
              </div>
            </div>
          )}
          {image.virus_scan_result === 'yet' && (
            <div className={cx(classes.virusScanResult, classes.yet)}>
              <Trans
                i18nKey="trans.添付ファイル<br/>{{fileName}}<br/>をスキャン中..."
                values={{ fileName: fileNameText(image.file_name) }}
              />
            </div>
          )}
        </div>
      )}
      {onDelete && (
        <Button
          color="primary"
          className={classes.deleteButton}
          onClick={deleteHandler}
        >
          {deleteButtonLabel}
        </Button>
      )}
    </Paper>
  )
}

export default Thumbnail
